// Authentication service for managing user authentication
export interface AuthUser {
  email: string;
  role: 'Admin' | 'Operator';
}

export interface AuthStatus {
  authenticated: boolean;
  user?: AuthUser;
}

class AuthService {
  private baseUrl = '/api/auth';

  /**
   * Request an OTP to be sent to the specified email
   */
  async requestOtp(email: string): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/request-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to request OTP');
    }

    return response.json();
  }

  /**
   * Verify OTP and create authenticated session
   */
  async verifyOtp(email: string, otpCode: string): Promise<{ message: string; user: AuthUser }> {
    const response = await fetch(`${this.baseUrl}/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otpCode }),
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to verify OTP');
    }

    return response.json();
  }

  /**
   * Check current authentication status
   */
  async getStatus(): Promise<AuthStatus> {
    try {
      const response = await fetch(`${this.baseUrl}/status`, {
        credentials: 'include',
      });

      if (!response.ok) {
        return { authenticated: false };
      }

      return response.json();
    } catch (error) {
      return { authenticated: false };
    }
  }

  /**
   * Logout and clear session
   */
  async logout(): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  }
}

export const authService = new AuthService();
